import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import { Eye, EyeOff, Lock, Mail, LogIn, Shield, Users } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import GradientBox from '../../components/ui/GradientBox';
import CustomButton from '../../components/ui/CustomButton';

const LoginPage = () => {
  const { login, isAuthenticated, isLoading, error, clearError } = useAuth();
  const { colors } = useTheme(); // Removed 'isDarkMode'
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleInputChange = e => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'rememberMe' ? checked : value,
    }));
    if (error) clearError();
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);

    const result = await login(formData.email, formData.password);

    if (result.success) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }

    setIsSubmitting(false);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ background: colors.background.gradient }}
      >
        <CircularProgress size={60} sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: colors.background.gradient,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 3,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 900, display: 'flex', gap: 4 }}>
        {/* Left Side - Branding */}
        <GradientBox
          glass
          sx={{
            flex: 1,
            padding: 4,
            borderRadius: 3,
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Shield
            size={80}
            color={colors.primary.main}
            style={{ marginBottom: 24 }}
          />

          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: colors.primary.gradient,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Certificate Management
          </Typography>

          <Typography variant="h6" sx={{ mb: 3, opacity: 0.8 }}>
            Secure • Modern • Efficient
          </Typography>

          <Typography variant="body1" sx={{ opacity: 0.7, mb: 4 }}>
            Professional certificate of conformity management system with
            role-based access control and modern security features.
          </Typography>

          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Users size={32} color={colors.success.main} />
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                User Management
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Lock size={32} color={colors.warning.main} />
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Secure Access
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Shield size={32} color={colors.info.main} />
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Certificate Control
              </Typography>
            </Box>
          </Box>
        </GradientBox>

        {/* Right Side - Login Form */}
        <GradientBox
          glass
          sx={{
            flex: { xs: 1, md: 0.8 },
            padding: 4,
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <LogIn
              size={48}
              color={colors.primary.main}
              style={{ marginBottom: 16 }}
            />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              Welcome Back
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Sign in to your account to continue
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={clearError}
            >
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              name="email"
              type="email"
              label="Email Address"
              value={formData.email}
              onChange={handleInputChange}
              required
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Mail size={20} color={colors.primary.main} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              name="password"
              type={showPassword ? 'text' : 'password'}
              label="Password"
              value={formData.password}
              onChange={handleInputChange}
              required
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock size={20} color={colors.primary.main} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                />
              }
              label="Remember me"
              sx={{ mb: 3 }}
            />

            <CustomButton
              type="submit"
              variant="gradient"
              gradient="primary"
              fullWidth
              size="large"
              disabled={isSubmitting}
              glow
              sx={{ mb: 3, py: 1.5 }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Sign In'
              )}
            </CustomButton>
          </form>

          <Divider sx={{ my: 3 }}>
            <Typography variant="caption" color="textSecondary">
              Role-Based Access
            </Typography>
          </Divider>

          {/* Role Information */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Access levels:
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: colors.primary.main }}
                >
                  ADMIN
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color="textSecondary"
                >
                  User Management
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: colors.secondary.main }}
                >
                  OFFICE
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color="textSecondary"
                >
                  Certificate Management
                </Typography>
              </Box>
            </Box>
          </Box>
        </GradientBox>
      </Box>
    </Box>
  );
};

export default LoginPage;
